// src/lib/bootstrapRetry.ts
//
// Resilience helpers for the app-bootstrap network calls. A sub-second network
// interruption during boot used to wedge the app on "Loading..." forever: a
// profile fetch failed at the transport layer (`TypeError: Failed to fetch`),
// nothing retried, and there was no exit. These helpers make transport failures
// fail fast and retry, while letting a real HTTP / PostgREST answer (401, 403,
// PGRST*) surface immediately instead of being retried.
//
// No new dependencies; no auth/session/token logic lives here.

// Per-request hang budget. A request that neither resolves nor rejects is the
// most dangerous failure mode — a retry wrapper does nothing for a promise that
// never settles. Aborting turns a hang into a retryable transport error.
export const BOOTSTRAP_ABORT_MS = 8000;

// Total attempts across the retry sequence.
const MAX_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// True only for transport-level failures — a thrown/returned network error or an
// aborted request. A genuine PostgREST / HTTP answer carries a non-empty error
// `code` (e.g. 'PGRST116', '42501', '401'); those are real answers and must never
// be retried.
export function isTransportError(e: unknown): boolean {
  if (!e) return false;
  const err = e as { code?: unknown; name?: unknown; message?: unknown };
  if (err.code !== undefined && err.code !== null && err.code !== '') return false;
  const name = String(err.name ?? '').toLowerCase();
  if (name === 'typeerror' || name === 'aborterror' || name === 'timeouterror') return true;
  const msg = String(err.message ?? e).toLowerCase();
  return /failed to fetch|network ?error|err_network|load failed|fetch failed|aborted|the operation was aborted|timed out|timeout|signal timed out/.test(
    msg
  );
}

export interface BootstrapAbort {
  signal: AbortSignal;
  clear: () => void;
}

// AbortSignal.timeout() is not in this project's browser target (Vite's default
// build target includes Safari 14 / Chrome 87, which predate it), so build the
// timeout from an AbortController and clear the timer on settle to avoid a
// dangling abort firing after the request already finished.
export function bootstrapAbort(ms: number = BOOTSTRAP_ABORT_MS): BootstrapAbort {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timer),
  };
}

// Run `run` with a per-request abort budget, clearing the timer on settle.
export function withAbort<T>(run: (signal: AbortSignal) => PromiseLike<T>): Promise<T> {
  const ab = bootstrapAbort();
  return Promise.resolve(run(ab.signal)).finally(ab.clear);
}

// Run `fn` up to MAX_ATTEMPTS times, retrying ONLY when it throws a transport-level
// error. A resolved value returns immediately — including a resolved PostgREST/HTTP
// error object, which is a real answer, not a network failure. Backoff is
// exponential (~1s, then ~2s) with +/-25% jitter. `onRetry` is an optional
// observation hook (e.g. logging) fired before each backoff wait.
export async function withBootstrapRetry<T>(
  fn: () => Promise<T>,
  onRetry?: (attempt: number, err: unknown) => void
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      // A real answer (or a genuine bug) is not retryable — surface it now.
      if (!isTransportError(err)) throw err;
      if (attempt < MAX_ATTEMPTS - 1) {
        if (onRetry) onRetry(attempt + 1, err);
        const base = 1000 * Math.pow(2, attempt); // 1000, 2000 (exponential; ~4000 shape)
        const jitter = base * 0.25 * Math.random();
        await sleep(base + jitter);
      }
    }
  }
  throw lastErr;
}
